import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {FormsModule} from "@angular/forms";
import {NgForOf, NgIf, NgTemplateOutlet} from "@angular/common";
import {debounceTime, Subject, Subscription} from "rxjs";
import {filter, take} from "rxjs/operators";
import {WebsocketService} from "../../services/websocket.service";
import { Location } from '@angular/common';
import { AnimationService } from '../../services/animation.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-input-edit',
  templateUrl: './input-edit.component.html',
  styleUrls: ['./input-edit.component.scss'],
  standalone: true,
  imports: [TranslatePipe, FormsModule, NgForOf, NgIf, NgTemplateOutlet]
})

export class InputEditComponent implements OnInit {
  input: any;
  outputs: any[] = [];
  scenarios: any[] = [];
  controllerMac: string | null = null;
  // Counter: the reading as a human reads it (in the configured unit), and the raw pulse
  // count the device actually keeps.
  counterReading: number | null = null;
  counterPulses: number | null = null;
  controllerModel: string = '';
  firmwareVersion: string = '';
  deletingAction: { eventIndex: number, actionIndex: number } | null = null;
  addingAction: { eventIndex: number, actionIndex: number } | null = null;
  swappingActions: { eventIndex: number, from: number, to: number, direction: 'up' | 'down' } | null = null;
  formError: string = '';
  //private toggleSubject = new Subject<{ payload:any }>();
private routeSub: Subscription | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private websocketService: WebsocketService,
    private location: Location,
    private animationService: AnimationService
  ) {    
  }
  
  ngOnInit(): void {
    const eid = this.route.snapshot.paramMap.get('eid');
    if (eid) {
      console.log('input uuid ', eid);
      this.websocketService.getUserDevices().pipe(filter((devices: any) => devices != null), 
                                                         take(1)).subscribe((devices) => {
        console.log('d', devices);
        if (devices == null)
          return;
        // Find the device and input with matching uuid
        let currentDevice: any = null;
        for (const device of devices) {             
          if (device && device.config.inputs) {
            this.input = device.config.inputs.find((input: any) => input.eid === eid);
            if (this.input) {
              currentDevice = device;
              this.controllerModel = device.model ?? '';
              this.firmwareVersion = device.info?.version ?? '';
              // This was never set — it was only ever read (back() worked because it falls
              // back to history state, a command did not: it went out with mac = null).
              this.controllerMac = device.mac ?? (typeof eid === 'string' ? eid.substring(0, 12) : null);
              // The counter's reading is runtime state, not config: take it from the stored
              // states the cloud keeps, so it can be edited even while the device is offline.
              const stored = device.ioStates?.countersStates?.[String(this.input.id)];
              this.counterPulses = stored ?? null;
              this.counterReading = stored !== undefined && stored !== null
                ? stored * this.perPulse : null;
              break;
            }
          }
        }
        
        // Collect outputs from all devices with the same groupID as current device
        if (currentDevice) {
          console.log('currentDevice', currentDevice);
          this.outputs = [];
          this.scenarios = currentDevice.config.scenarios;       
          
          const currentGroupID = currentDevice.groupID;
          
          // Если groupID нет, берем аутпуты ТОЛЬКО текущего устройства
          if (currentGroupID === undefined || currentGroupID === null || currentGroupID === '') {
            if (currentDevice.config?.outputs) {
              this.outputs.push(...currentDevice.config.outputs);
            }
          } else {
            // Если groupID есть, собираем аутпуты со всех устройств группы
            for (const device of devices) {
              if (device?.groupID === currentGroupID && device?.config?.outputs) {
                this.outputs.push(...device.config.outputs);
              }
            }   
          }
          // for (const device of devices) {
          //   if (device?.groupID === currentGroupID && device?.config.outputs) {
          //     this.outputs.push(...device.config.outputs);
          //   }
          // }   
          
          this.updateEvents();
          //console.log('outputs', this.outputs);
        }      
      });
    }    
  }

  updateEvents(): void {
    //console.log(this.input.events);
    // Для InvertedSwitch: только toggle
    if (this.input.type === 'InvertedSwitch') {
      const originalToggle = this.input.events?.find((e: { event: string }) => e.event === 'toggle');
      this.input.events = [this.keepEvent('toggle', originalToggle)];
    } else if (this.input.type === 'Switch') {
      // Для SW: on и off
      const originalOn = this.input.events?.find((e: { event: string }) => e.event === 'on');
      const originalOff = this.input.events?.find((e: { event: string }) => e.event === 'off');
      this.input.events = [
        this.keepEvent('on', originalOn),
        this.keepEvent('off', originalOff)
      ];
    } else if (this.input.type === 'Button') {
      // Для BTN: toggle и longpress
      const originalToggle = this.input.events?.find((e: { event: string }) => e.event === 'toggle');
      const originalLongpress = this.input.events?.find((e: { event: string }) => e.event === 'longpress');
      this.input.events = [
        this.keepEvent('toggle', originalToggle),
        this.keepEvent('longpress', originalLongpress)
      ];
    } else if (this.input.type === 'Counter') {
      this.input.events = [];
    }
  }

  /** An event slot for the input type, keeping what the old event had (actions, sequence). */
  private keepEvent(event: string, original: any): any {
    const e: any = { event, actions: original?.actions || [], uuid: original?.uuid };
    if (original?.sequence?.length) e.sequence = original.sequence;
    return e;
  }

  /** Sequences run on the controller itself — firmware 3.3.0 and newer. */
  supportsSequences(): boolean {
    const m = /^v?(\d+)\.(\d+)/.exec(this.firmwareVersion || '');
    if (!m) return false;
    const [maj, min] = [+m[1], +m[2]];
    return maj > 3 || (maj === 3 && min >= 3);
  }

  isRCV2(): boolean {
    return this.controllerModel?.startsWith('RCV2') ?? false;
  }
  
  addAction(eventIndex: number): void {
    const actions = this.input.events[eventIndex].actions;
    // const maxOrder = actions.reduce((max: number, action: any) => {
    //   return action.order > max ? action.order : max;
    // }, -1);

    // Set default action based on input type and event
    let defaultAction = 'toggle';
    if (this.input.type === 'Switch') {
      defaultAction = this.input.events[eventIndex].event === 'on' ? 'on' : 'off';
    }

    actions.push({      
      action: defaultAction,      
      type: 'output',
      //order: maxOrder + 1
    });

    // Set the adding animation state
    this.addingAction = { eventIndex, actionIndex: actions.length - 1 };
    setTimeout(() => {
      this.addingAction = null;
    }, 300); // Match this with the CSS animation duration
  }

  addStep(eventIndex: number): void {
    const event = this.input.events[eventIndex];
    if (!event.sequence) event.sequence = [];
    event.sequence.push({ actions: [] });
    this.addStepAction(eventIndex, event.sequence.length - 1);
  }

  removeStep(eventIndex: number, stepIndex: number): void {
    const event = this.input.events[eventIndex];
    event.sequence.splice(stepIndex, 1);
    if (!event.sequence.length) delete event.sequence;
  }

  addStepAction(eventIndex: number, stepIndex: number): void {
    const event = this.input.events[eventIndex];
    let defaultAction = 'toggle';
    if (this.input.type === 'Switch') defaultAction = event.event === 'on' ? 'on' : 'off';
    event.sequence[stepIndex].actions.push({ action: defaultAction, type: 'output' });
  }

  /** Remove button of the shared action editor: `at.s` set means a sequence step's action. */
  removeActionAt(at: { e: number, s?: number, a: number }): void {
    if (at.s === undefined) {
      this.removeAction(at.e, at.a);
      return;
    }
    this.input.events[at.e].sequence[at.s].actions.splice(at.a, 1);
  }

  onOutputChange(action: any): void {
    const selectedOutput = this.outputs.find(o => o.outputID === action.outputID);
    action.output = selectedOutput?.id || null;    
  }

  removeAction(eventIndex: number, actionIndex: number): void {
    this.deletingAction = { eventIndex, actionIndex };
    setTimeout(() => {
      this.input.events[eventIndex].actions.splice(actionIndex, 1);
      this.deletingAction = null;
    }, 300); // Match this with the CSS animation duration
  }

  isDeleting(eventIndex: number, actionIndex: number): boolean {
    return this.deletingAction?.eventIndex === eventIndex && 
           this.deletingAction?.actionIndex === actionIndex;
  }

  isAdding(eventIndex: number, actionIndex: number): boolean {
    return this.addingAction?.eventIndex === eventIndex && 
           this.addingAction?.actionIndex === actionIndex;
  }

  moveActionUp(eventIndex: number, actionIndex: number): void {
    const actions = this.input.events[eventIndex].actions;
    if (actionIndex > 0) {
      this.swappingActions = { eventIndex, from: actionIndex, to: actionIndex - 1, direction: 'up' };
      setTimeout(() => {
        // Swap orders
        const currentOrder = actions[actionIndex].order;
        actions[actionIndex].order = actions[actionIndex - 1].order;
        actions[actionIndex - 1].order = currentOrder;
        // Swap positions in array
        [actions[actionIndex], actions[actionIndex - 1]] = [actions[actionIndex - 1], actions[actionIndex]];
        this.swappingActions = null;
      }, 300); // 10x slower
    }
  }

  moveActionDown(eventIndex: number, actionIndex: number): void {
    const actions = this.input.events[eventIndex].actions;
    if (actionIndex < actions.length - 1) {
      this.swappingActions = { eventIndex, from: actionIndex, to: actionIndex + 1, direction: 'down' };
      setTimeout(() => {
        // Swap orders
        const currentOrder = actions[actionIndex].order;
        actions[actionIndex].order = actions[actionIndex + 1].order;
        actions[actionIndex + 1].order = currentOrder;
        // Swap positions in array
        [actions[actionIndex], actions[actionIndex + 1]] = [actions[actionIndex + 1], actions[actionIndex]];
        this.swappingActions = null;
      }, 300); // 10x slower
    }
  }

  getSwapDirection(eventIndex: number, actionIndex: number): string | null {
    if (!this.swappingActions || this.swappingActions.eventIndex !== eventIndex) return null;
    if (this.swappingActions.from === actionIndex) return this.swappingActions.direction;
    if (this.swappingActions.to === actionIndex) return this.swappingActions.direction === 'up' ? 'down' : 'up';
    return null;
  }

  isSwapping(eventIndex: number, actionIndex: number): boolean {
    return !!(this.swappingActions && this.swappingActions.eventIndex === eventIndex &&
      (this.swappingActions.from === actionIndex || this.swappingActions.to === actionIndex));
  }

  canMoveUp(eventIndex: number, actionIndex: number): boolean {
    return actionIndex > 0;
  }

  canMoveDown(eventIndex: number, actionIndex: number): boolean {
    return actionIndex < this.input.events[eventIndex].actions.length - 1;
  }

  isBtn(): boolean {    
    return this.input.id > 15
  }

  /** One pulse in the configured unit (a water meter usually ticks every 0.01 m³). */
  get perPulse(): number {
    const v = Number(this.input?.pulseValue);
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  /**
   * The reading is NOT part of the config: it is the device's runtime counter. So it is not
   * saved with Save — it is sent to the controller as a command, right now.
   */
  setCounter(): void {
    if (this.counterReading === null || !Number.isFinite(Number(this.counterReading))) return;

    const pulses = Math.round(Number(this.counterReading) / this.perPulse);
    this.websocketService.sendMessage({
      type: 'COMMAND',
      payload: {
        mac: this.controllerMac,
        command: 'SETCOUNTER',
        counterId: this.input.id,
        counterValue: pulses,
      },
    });
    this.counterPulses = pulses;
  }

  save(): void {
    this.formError = '';
    if (this.input.name == null) {
      this.formError = 'input.edit.err.name_required';
      return;
    }

    // A step with no actions is just a press that does nothing — drop it. One step left is
    // not a sequence (that is what the plain actions are for).
    for (const event of this.input.events ?? []) {
      if (!event.sequence) continue;
      event.sequence = event.sequence.filter((st: any) => st.actions?.length);
      if (event.sequence.length === 0) {
        delete event.sequence;
      } else if (event.sequence.length < 2) {
        this.formError = 'input.edit.err.sequence_min_steps';
        return;
      }
    }

    // The counter reading is not part of the config, so uploading the config would never
    // carry it. Saving the input while it was changed must still apply it — send it as the
    // command it is.
    if (this.input.type === 'Counter' && this.counterReading !== null) {
      const pulses = Math.round(Number(this.counterReading) / this.perPulse);
      if (pulses !== this.counterPulses) this.setCounter();
    }
    console.log('Saving input:', this.input);
    this.websocketService.sendMessage({
        type: 'UPDATEINPUT',
        payload: this.input,
      });
    //this.toggleSubject.next({ payload: this.input });
    this.back();    
  }

  back(): void {
    this.animationService.triggerLeaveAnimation();
    setTimeout(() => {
      const fromTab = (window.history && (window.history.state as any)?.fromTab) ? (window.history.state as any).fromTab : undefined;
      const mac = this.controllerMac || (window.history && (window.history.state as any)?.controllerMac);
      if (mac) {
        if (fromTab) {          
          this.router.navigate(['/controller', mac], { state: { activeTab: fromTab } });
        } else {
          this.router.navigate(['/controller', mac]);
        }
      } else {
        this.location.back();
      }
    }, 150); // Half the animation duration for smoother transition
  }
}
